/**
 * Service for interacting with Hello Retail API
 * Handles user tracking ID and recommendation fetching
 */
class HelloRetailService {
    constructor() {
        this.websiteUuid = "79627f54-060f-44b1-a07a-4c920ad803dd";
        // Separate search keys for analytics: mobile/tablet vs desktop
        const isMobile = window.matchMedia('(max-width: 1024px)').matches;
        this.searchKey = isMobile
            ? "80cbf4eb-fb43-4a23-83a7-f7c69a46008f"   // mobile + tablet
            : "768afbd2-89de-4d6d-8680-ec987bba247b";   // desktop
        this.powerstepKey = "k6a841b167e37130c28b1d9a3";
        this.storageKey = 'hr_uid';
    }

    /**
     * Gets or creates the Hello Retail User ID.
     * Cached as a promise so the lookup only happens once per page load.
     * @returns {Promise<string|null>}
     */
    async getTrackingUserId() {
        if (!this._uidPromise) {
            this._uidPromise = this._resolveTrackingUserId();
        }
        return this._uidPromise;
    }

    /**
     * Internal resolver — called once and cached by getTrackingUserId.
     * @returns {Promise<string|null>}
     */
    async _resolveTrackingUserId() {
        // Try local storage and cookie first
        let uid = localStorage.getItem(this.storageKey) || this.getCookie(this.storageKey);

        if (uid) return uid;

        // If not found, fetch from API
        try {
            const response = await fetch("https://core.helloretail.com/serve/trackingUser", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ websiteUuid: this.websiteUuid })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.id) {
                    uid = data.id;
                    localStorage.setItem(this.storageKey, uid);
                    document.cookie = `${this.storageKey}=${uid}; path=/; max-age=31536000`; // 1 year
                    return uid;
                }
            }
        } catch (error) {
            console.error('HelloRetailService: Error fetching tracking user ID:', error);
        }

        return null;
    }

    /**
     * Helper to get cookie value
     */
    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    /**
     * Fetches recommendations from Hello Retail
     * @param {Object} params - Parameters for the recommendation request
     * @param {string} [params.key] - Recommendation key from Hello Retail dashboard (e.g. product box placement)
     * @param {string} [params.awsKey] - The recommendation strategy key (legacy/unmanaged fallback)
     * @param {Array<number|string>} [params.productIds] - Context product IDs
     * @param {number} [params.count] - Number of products to fetch
     * @param {Object} [params.filters] - Optional filters to apply (e.g. { inStock: "true" })
     * @param {string} [params.sourceType] - Product source type (default: 'BOUGHT_TOGETHER')
     * @returns {Promise<Array<Object>|null>} List of recommended products or null
     */
    async fetchRecommendations({ key, awsKey, productIds = [], count = 10, filters = {}, sourceType = 'BOUGHT_TOGETHER' }) {
        try {
            const trackingUserId = await this.getTrackingUserId();

            // Convert IDs to numbers if they are strings
            const numericIds = productIds.map(id => Number(id)).filter(id => !isNaN(id));

            const fields = ["productNumber", "url", "id", "title", "imgUrl", "price", "oldPrice", "brand", "currency", "extraData"];

            let requestObj;

            if (key) {
                requestObj = {
                    key: key,
                    format: 'json',
                    fields: fields
                };
                if (numericIds.length > 0) {
                    requestObj.context = {
                        productNumbers: numericIds
                    };
                }
            } else {
                if (numericIds.length === 0) {
                    console.warn('HelloRetailService: No valid product IDs provided');
                    return null;
                }

                const sourceObj = {
                    "type": sourceType
                };

                // Medicine filtering is managed in the Hello Retail dashboard.
                // Pass through any caller-provided filters as-is.
                if (filters && Object.keys(filters).length > 0) {
                    sourceObj.filters = filters;
                }

                requestObj = {
                    "trackingKey": awsKey,
                    "fields": fields,
                    "count": count,
                    "context": {
                        "productNumbers": numericIds
                    },
                    "sources": [sourceObj]
                };
            }

            const payload = {
                "websiteUuid": this.websiteUuid,
                "trackingUserId": trackingUserId,
                "requests": [requestObj]
            };

            const response = await fetch('https://core.helloretail.com/serve/recoms', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success && data.responses && data.responses.length > 0) {
                const responseData = data.responses[0];
                if (responseData.products && responseData.products.length > 0) {
                    return {
                        products: responseData.products,
                        trackingKey: responseData.trackingKey || key || null
                    };
                }
            }
        } catch (error) {
            console.error('HelloRetailService: Error fetching recommendations:', error);
        }

        return null;
    }
}

// Expose globally
window.HelloRetailService = new HelloRetailService();
